import { Box, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { CoverageBadge } from './CoverageBadge'
import { vizDiv } from '../shared/viz'
import type { SeatResult } from '../../types/electionSim'

/**
 * Pure seat classifier shared by the seat board and the allocation table. Only **Modeled**
 * seats are targetable; the win-probability band splits them into:
 *   - Must-hold: 50–65% (leaning our way but not safe — protect the lead)
 *   - Reach:     35–50% (behind but flippable with effort)
 * Everything else (safe Modeled, Lean-only, Insufficient) falls to `rest`.
 * Null-guarded: a JSON-null seats list yields empty buckets rather than throwing.
 */
export function groupSeats(seats: SeatResult[]): {
  mustHold: SeatResult[]
  reach: SeatResult[]
  rest: SeatResult[]
} {
  const mustHold: SeatResult[] = []
  const reach: SeatResult[] = []
  const rest: SeatResult[] = []
  for (const s of seats ?? []) {
    if (s.coverage === 'Modeled' && s.winProbability >= 0.5 && s.winProbability <= 0.65) {
      mustHold.push(s)
    } else if (s.coverage === 'Modeled' && s.winProbability >= 0.35 && s.winProbability < 0.5) {
      reach.push(s)
    } else {
      rest.push(s)
    }
  }
  return { mustHold, reach, rest }
}

/** One seat row: name, thin win-prob bar with a centered 50% tick, win %, and coverage pill. */
function SeatRow({ seat }: { seat: SeatResult }) {
  const pct = Math.round(seat.winProbability * 100)
  return (
    <HStack gap={3} py={1.5}>
      <Text fontSize="sm" flex="1" minW={0} lineClamp={1} title={seat.seatName}>
        {seat.seatName}
      </Text>
      <Box position="relative" w="72px" h="6px" bg="gray.100" borderRadius="full" flexShrink={0}>
        <Box
          position="absolute"
          left={0}
          top={0}
          h="100%"
          borderRadius="full"
          bg={vizDiv.pos}
          w={`${Math.max(0, Math.min(100, pct))}%`}
        />
        {/* 50% reference tick */}
        <Box
          position="absolute"
          left="50%"
          top="-2px"
          h="10px"
          w="2px"
          bg="gray.400"
          transform="translateX(-50%)"
        />
      </Box>
      <Text fontSize="sm" fontWeight="semibold" w="42px" textAlign="right" flexShrink={0}>
        {pct}%
      </Text>
      <CoverageBadge coverage={seat.coverage} />
    </HStack>
  );
}

interface SeatColumnProps {
  title: string
  subtitle: string
  seats: SeatResult[]
}

function SeatColumn({ title, subtitle, seats }: SeatColumnProps) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
      <Text fontSize="sm" fontWeight="bold">
        {title}
      </Text>
      <Text fontSize="xs" color="gray.500" mb={2}>
        {subtitle}
      </Text>
      {seats.length === 0 ? (
        <Text fontSize="xs" color="gray.400">
          No seats in this band.
        </Text>
      ) : (
        <VStack align="stretch" gap={0}>
          {seats.map((s) => (
            <SeatRow key={s.seatCode} seat={s} />
          ))}
        </VStack>
      )}
    </Box>
  );
}

export interface SeatBoardProps {
  seats: SeatResult[]
}

/**
 * Chamber seat board (spec §slice-4): three columns — Must-hold, Reach, and a coverage legend —
 * so an organizer sees at a glance which Modeled seats need protecting vs. flipping, and how each
 * seat's confidence is rated. Print-safe (`.profile-card`).
 */
export function SeatBoard({ seats }: SeatBoardProps) {
  const { mustHold, reach } = groupSeats(seats)
  return (
    <Box className="profile-card" bg="surface" p={6} borderRadius="lg" shadow="md">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
        Seat board
      </Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
        <SeatColumn title="Must-hold" subtitle="Modeled · 50–65% win prob" seats={mustHold} />
        <SeatColumn title="Reach" subtitle="Modeled · 35–50% win prob" seats={reach} />
        <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Coverage legend
          </Text>
          <VStack align="stretch" gap={2}>
            <HStack gap={2}>
              <CoverageBadge coverage="Modeled" />
              <Text fontSize="xs" color="gray.600">
                Precinct-level model
              </Text>
            </HStack>
            <HStack gap={2}>
              <CoverageBadge coverage="LeanOnly" />
              <Text fontSize="xs" color="gray.600">
                Rated from district lean
              </Text>
            </HStack>
            <HStack gap={2}>
              <CoverageBadge coverage="Insufficient" />
              <Text fontSize="xs" color="gray.600">
                Not enough data to rate
              </Text>
            </HStack>
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}
